import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';
 
const ANIMATION_OPTIONS = {
  duration: 500,
};
 
/**
 * Ref attributes that belong to this component. Generated copies must never carry them:
 * the base Component re-queries `[ref]` on every DOM mutation, so a cloned
 * `ref="marqueeItems[]"` inflates `refs.marqueeItems` and corrupts the copy count.
 */
const OWN_REF_SELECTOR = '[ref="wrapper"], [ref="content"], [ref="marqueeItems[]"]';
 
/**
 * A custom element that displays a marquee.
 *
 * Victory Creative fix (Sept 2026): the stock version counted copies via refs (which include
 * clones once the MutationObserver refreshes them) and rebuilt on every window `resize`,
 * including the height-only resizes mobile browsers fire when the URL bar collapses and
 * expands on scroll. The second rebuild removed every item, including the original.
 * This version keeps an explicit source element, counts copies from the DOM, strips refs
 * from clones, rebuilds idempotently, and only reacts to real width changes.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper - The wrapper element.
 * @property {HTMLElement} content - The content element.
 * @property {HTMLElement[]} marqueeItems - The marquee items collection.
 *
 * @extends Component<Refs>
 */
class MarqueeComponent extends Component {
  requiredRefs = ['wrapper', 'content', 'marqueeItems'];
 
  /**
   * The authored item group that every copy is cloned from.
   * @type {HTMLElement | null}
   */
  #source = null;
 
  /** @type {number} */
  #lastWidth = 0;
 
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;
 
  connectedCallback() {
    super.connectedCallback();
 
    const { marqueeItems } = this.refs;
    if (!marqueeItems || marqueeItems.length === 0) return;
 
    this.#source = marqueeItems[0];
    this.#lastWidth = this.offsetWidth;
    this.#build();
 
    this.#resizeObserver = new ResizeObserver(this.#handleResize);
    this.#resizeObserver.observe(this);
    this.addEventListener('pointerenter', this.#slowDown);
    this.addEventListener('pointerleave', this.#speedUp);
  }
 
  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#rebuild.cancel();
    this.removeEventListener('pointerenter', this.#slowDown);
    this.removeEventListener('pointerleave', this.#speedUp);
  }
 
  /**
   * @type {{ cancel: () => void, current: number } | null}
   */
  #animation = null;
 
  #slowDown = debounce(() => {
    if (this.#animation) return;
 
    const animation = this.refs.wrapper.getAnimations()[0];
 
    if (!animation) return;
 
    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from: 1,
      to: 0,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }, ANIMATION_OPTIONS.duration);
 
  #speedUp() {
    this.#slowDown.cancel();
 
    const animation = this.refs.wrapper.getAnimations()[0];
 
    if (!animation || animation.playbackRate === 1) return;
 
    const from = this.#animation?.current ?? 0;
    this.#animation?.cancel();
 
    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from,
      to: 1,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }
 
  /**
   * Only width matters to the marquee. Height-only changes (mobile URL bar showing/hiding
   * during scroll) are ignored so the animation is never reset mid-scroll.
   */
  #handleResize = () => {
    const width = this.offsetWidth;
    if (Math.abs(width - this.#lastWidth) < 1) return;
 
    this.#lastWidth = width;
    this.#rebuild();
  };
 
  #rebuild = debounce(() => {
    if (!this.isConnected || !this.#source) return;
 
    if (this.#calculateNumberOfCopies() !== this.#countCopies()) {
      this.#build();
      this.#restartAnimation();
    } else {
      this.#setSpeed();
    }
  }, 250);
 
  /**
   * Regenerates all copies from the source. Safe to call repeatedly: anything generated by a
   * previous build is removed first, and the source element itself is never touched.
   */
  #build() {
    const source = this.#source;
    const { wrapper, content } = this.refs;
    if (!source || !wrapper || !content) return;
 
    for (const child of Array.from(content.children)) {
      if (child !== source) child.remove();
    }
 
    for (const child of Array.from(wrapper.children)) {
      if (child !== content) child.remove();
    }
 
    const copies = this.#calculateNumberOfCopies();
 
    for (let i = 1; i < copies; i++) {
      const copy = cloneWithoutOwnRefs(source);
      copy.setAttribute('aria-hidden', 'true');
      content.appendChild(copy);
    }
 
    const duplicate = cloneWithoutOwnRefs(content);
    duplicate.setAttribute('aria-hidden', 'true');
    wrapper.appendChild(duplicate);
 
    this.#setSpeed();
  }
 
  #countCopies() {
    return this.refs.content?.querySelectorAll(':scope > .marquee__repeated-items').length ?? 0;
  }
 
  #restartAnimation() {
    const animations = this.refs.wrapper.getAnimations();
 
    requestAnimationFrame(() => {
      for (const animation of animations) {
        animation.currentTime = 0;
      }
    });
  }
 
  #setSpeed(value = this.#calculateSpeed()) {
    this.style.setProperty('--marquee-speed', `${value}s`);
  }
 
  #calculateSpeed() {
    const speedFactor = Number(this.getAttribute('data-speed-factor'));
    return Math.sqrt(this.#calculateNumberOfCopies()) * speedFactor;
  }
 
  #calculateNumberOfCopies() {
    const marqueeWidth = this.offsetWidth;
    const itemWidth = this.#source?.offsetWidth ?? 0;
 
    return itemWidth === 0 ? 1 : Math.max(1, Math.ceil(marqueeWidth / itemWidth));
  }
}
 
/**
 * Deep-clones an element and strips this component's ref attributes from the copy.
 * @param {HTMLElement} element
 * @returns {HTMLElement}
 */
function cloneWithoutOwnRefs(element) {
  const clone = /** @type {HTMLElement} */ (element.cloneNode(true));
 
  clone.removeAttribute('ref');
  for (const node of clone.querySelectorAll(OWN_REF_SELECTOR)) {
    node.removeAttribute('ref');
  }
 
  return clone;
}
 
/**
 * Animate a numeric property smoothly.
 * @param {Object} params - The parameters for the animation.
 * @param {number} params.from - The starting value.
 * @param {number} params.to - The ending value.
 * @param {number} params.duration - The duration of the animation in milliseconds.
 * @param {function(number): void} params.onUpdate - The function to call on each update.
 * @param {function(number): number} [params.easing] - The easing function.
 * @param {function(): void} [params.onComplete] - The function to call when the animation completes.
 */
function animateValue({ from, to, duration, onUpdate, easing = (t) => t * t * (3 - 2 * t), onComplete }) {
  const startTime = performance.now();
  let cancelled = false;
  let currentValue = from;
 
  /**
   * @param {number} currentTime - The current time in milliseconds.
   */
  function animate(currentTime) {
    if (cancelled) return;
 
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easing(progress);
    currentValue = from + (to - from) * easedProgress;
 
    onUpdate(currentValue);
 
    if (progress < 1) {
      requestAnimationFrame(animate);
    } else if (typeof onComplete === 'function') {
      onComplete();
    }
  }
 
  requestAnimationFrame(animate);
 
  return {
    get current() {
      return currentValue;
    },
    cancel() {
      cancelled = true;
    },
  };
}
 
if (!customElements.get('marquee-component')) {
  customElements.define('marquee-component', MarqueeComponent);
}